import { TFunction } from 'i18next';
import { TableCellProps } from 'material-hu';
import { InfiniteListProps } from 'src/components/list/InfiniteList';
import { User } from 'src/types/user';
import { Pagination } from 'src/types/services';
import { Attachment } from 'src/types/attachments';

export enum ExtensionTypeReport {
  XLSX = 'SLSX',
  CSV = 'CSV',
}

export enum VacationRequestSources {
  MY_TIME_OFF = 'MY_TIME_OFF',
  REQUEST_CREATION = 'REQUEST_CREATION',
  REQUEST_DETAIL = 'REQUEST_DETAIL',
}

export enum VacationActionsSources {
  MANAGEMENT_DETAIL = 'MANAGEMENT_DETAIL',
  MANAGEMENT_LIST = 'MANAGEMENT_LIST',
  COLLABORATOR_DETAIL = 'COLLABORATOR_DETAIL',
  COLLABORATOR_LIST = 'COLLABORATOR_LIST',
  MANAGEMENT_LIST_MASSIVE = 'MANAGEMENT_LIST_MASSIVE',
  MAIL = 'MAIL',
}

export enum ResponseCode {
  VACATION_APPROVER_NOT_AVAILABLE_FOR_USER = 'VACATION_APPROVER_NOT_AVAILABLE_FOR_USER',
  FROM_DATE_BEFORE_TODAY_NOT_ALLOWED = 'FROM_DATE_BEFORE_TODAY_NOT_ALLOWED',
  VACATION_REQUEST_NOT_PENDING = 'VACATION_REQUEST_NOT_PENDING',
  ERROR_MAX_ATTACHMENTS_SIZE_VACATIONS = 'ERROR_MAX_ATTACHMENTS_SIZE_VACATIONS',
}

export enum Role {
  MANAGER = 'MANAGER',
  COLLABORATOR = 'COLLABORATOR',
}

export enum OperationsBalance {
  ADDITION = 'ADDITION',
  SUBTRACTION = 'SUBTRACTION',
}

export enum UnitVacationes {
  DAYS = 'DAYS',
  HOURS = 'HOURS',
}

export enum AllowanceType {
  BASIC_ANNUAL = 'BASIC_ANNUAL',
  UNLIMITED = 'UNLIMITED',
}

export enum CountingMethod {
  CALENDAR_DAYS = 'CALENDAR_DAYS',
  BUSINESS_DAYS = 'BUSINESS_DAYS',
}

export enum StepStatus {
  PENDING = 'PENDING',
  WAITING_OTHER_STEP = 'WAITING_OTHER_STEP',
  REJECTED = 'REJECTED',
  APPROVED = 'APPROVED',
}

export enum VacationStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  APPROVED = 'APPROVED',
  CANCELLED = 'CANCELLED',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
}

export enum ReportType {
  BALANCES = 'balances',
  REQUESTS = 'requests',
}

export enum CriteriaSortBy {
  ASC_STATE = 'PENDING_FIRST',
  DESC_CREATED_AT = 'REQUESTED_DATE_RECENT',
  ASC_CREATED_AT = 'REQUESTED_DATE_OLDEST',
  DESC_FROM_DATE = 'START_DATE_FUTURE',
  ASC_FROM_DATE = 'START_DATE_OLDEST',
}

export enum SectionsManager {
  CALENDAR = 'CALENDAR',
  REQUESTS = 'REQUESTS',
  BALANCES = 'BALANCES',
}

export enum TabManagementType {
  MANAGEMENT_TAB_SIMPLE_DOWNLOAD = 'management_tab_simple_download',
  MANAGEMENT_TAB_ADVANCED_DOWNLOAD = 'management_tab_advanced_download',
}

export enum AttachmentRequirement {
  NONE = 'NONE',
  OPTIONAL = 'OPTIONAL',
  REQUIRED = 'REQUIRED',
}

export enum RequestedDaysErrors {
  TIME_OFF_OVERLAPPING_REQUEST = 'TIME_OFF_OVERLAPPING_REQUEST',
  TIME_OFF_NEW_HIRES_REQUESTS_BAN = 'TIME_OFF_NEW_HIRES_REQUESTS_BAN',
}

export enum ConsumptionType {
  FULL_DAY = 'FULL_DAY',
  HALF_DAY = 'HALF_DAY',
}

export type DateToRequest = {
  date: string;
  consumptionType: ConsumptionType;
};

export type CollaboratorOrManagerData = Partial<InfiniteListProps> & {
  data?: Vacations;
  policyTypes?: PolicyTypes[];
  countRequestManager?: number;
  hasRequests?: boolean;
};

export type ParamsDatesVacationCell = {
  date: string | Date;
  user: User;
  isUtc?: boolean;
  type: string;
};

export type ParamsGetCorrectDays = {
  amount: number;
  operation: string;
  t?: TFunction;
  onlyNumbers?: boolean;
};

export type AdjustAvailableTimeForm = {
  operation: string;
  amount: number;
  observations: string;
};

export type AdjustAvailableTimeParams = {
  policyTypeId: number;
  userId: number;
  operation: string;
  amount: number;
  observations: string;
};

export type PolicyTypes = {
  id: number;
  name: string;
  description: string;
  attachmentInstructions?: string;
  attachmentRequirement?: AttachmentRequirement;
  icon?: string;
  allowanceType: AllowanceType;
  /**
   * this represents the amount of time (in days or hours) that the user...
   *  has available (when AllowanceType == BASIC_ANNUAL) or
   *  has requested (when AllowanceType == UNLIMITED)
   */
  amount: number;
  unit: UnitVacationes;
  descriptionRequired: boolean;
  minimumAmountPerRequest?: number;
  maximumAmountPerRequest?: number;
  minimumBalance?: number;
  maximumBalance?: number;
  maximumRemnant?: number;
  countingMethod: CountingMethod;
  fromDateCycle: string;
  toDateCycle: string;
  allowHalfDayRequests?: boolean;
  noRetroactiveRequests?: boolean;
  amountRequested: number;
  currentBalance: number;
};

export type ResponseDetailPolicyTypes = {
  id: number;
  name: string;
  fromDateCycle: string;
  toDateCycle: string;
  unit: UnitVacationes;
  amountRequested: number;
  currentBalance: number;
  allowanceType: AllowanceType;
};

export type PolicyTypesBalance = {
  amount: number;
  eventType: string;
};

export type ResponseRequestTimeOff = {
  id: number;
  policyType: PolicyTypes;
  createdAt: string;
  from: DateToRequest;
  to: DateToRequest;
  resolutionReason?: string;
  state: VacationStatus;
  stepState: StepStatus | null;
  issuer?: User;
  approver?: User;
  description?: string;
  amountRequested?: number;
  amountAvailable?: number;
  canReview?: boolean;
  attachments: Attachment[];
};

export type ApprovalStep = {
  id: number;
  state: string;
  position: number;
  potentialApprovers: User[];
  potentialApproversCount: number;
};

export type VacationsNotificationsResponse = {
  vacationRequestsAsCollaboratorNotifications: number;
  vacationRequestsAsManagerNotifications: number;
};

export type TimeOffRequestedDaysResponse = {
  amount: number;
  errors: any;
};

export type BalanceReport = {
  amountRequested: number;
  currentBalance?: number;
  policy: Partial<PolicyTypes> & {
    accumulationFrequency: string;
    accumulationMoment: string;
    allowanceAmount: number;
    allowanceStart: string;
    approvalRequired: boolean;
    dismissalApportionmentMethod: string;
    hiringApportionmentMethod: string;
    peopleAmount: number;
    policyType: PolicyTypes;
  };
  user: User;
};

export type ListTitleTableVacations = {
  id: string;
  text: string;
  align?: TableCellProps['align'];
};

export type BalanceConformationCustomData = {
  fromDate: string;
  toDate: string;
  observations: string | null;
  responsibleUserId: number | null;
  fromDateCycle: string;
  toDateCycle: string;
};

export type BalanceConformationType = {
  id: number;
  amount: number;
  fromDate: string;
  toDate: string;
  expirationDate?: string;
};

export type RegionInfo = {
  createdAt: string;
  id: number;
  body: string;
  title: string;
  updatedAt: string;
};

export type CalendarRequests = {
  user: User;
  region: RegionInfo;
  requests: ResponseRequestTimeOff[];
  holidays: HolidaysInfo[];
};

export type HolidaysInfo = {
  name: string;
  date: string;
  title: string;
};

export type ParamsGetWidthToRenderRequestInCalendar = {
  request: ResponseRequestTimeOff;
  differentsMonth?: boolean;
  amountDaysInMonth?: number;
};

export type Policy = {
  id: number;
  name: string;
  policyType: PolicyTypes;
};

export type ExtraFieldsReport = {
  fields?: string;
  groupIds?: string;
  userFields?: string;
};

export enum VacationsRequestSort {
  CREATED_AT = 'CREATED_AT',
  FROM_DATE = 'FROM_DATE',
  STATE = 'STATE',
}

export type VacationsFilterValues = {
  users: User[];
  fromDate: string | null;
  toDate: string | null;
  policyTypeIds: Record<number, boolean>;
  policyIds: Record<number, boolean>;
  states: Partial<Record<VacationStatus, boolean>>;
  onlyPeopleWithRequests: boolean;
  orderBy?: VacationsRequestSort;
  order?: string;
};

export type TimeoffAdvancedReportValues = {
  enabledRequestFields: boolean;
  requestFields: Record<string, boolean>;
  enableSegmentation: boolean;
  segmentations: Record<string, boolean>;
  enableProfileFields: boolean;
  profileFields: Record<string, boolean>;
  extensionType: string;
};

export type Vacations = ResponseRequestTimeOff[];
export type BalanceConfomationResponse = Pagination<BalanceConformationType>;
export type VacationsPaginated = Pagination<Vacations>;
export type PolicyTypesResponse = PolicyTypes[];
export type PoliciesResponse = Policy[];
export type ResponsePolicyTypesBalance = PolicyTypesBalance[];
export type ResponseBalanceReport = Pagination<BalanceReport[]>;
export type ResponseRequestCalendar = Pagination<CalendarRequests[]>;
export type ApprovalStepsType = ApprovalStep[];
